import {css, StyleSheet} from 'aphrodite';
import gql from 'graphql-tag';
import React, {CSSProperties, useCallback, useMemo, useState} from 'react';

import {Permissions} from 'auth/PermissionConstants';
import {canRequestAccess, getArgForRequestAccess, requestAccess} from 'auth/RequestAccess';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {ReviewLifecycleType, ReviewStatus} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {
  ReviewBadgeContext,
  ReviewBadgeContextValue,
  ReviewError,
  ReviewErrorType,
} from 'notebook/reviews/ReviewBadgeContext';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {isReviewableEntry} from 'notebook/reviews/ReviewUtil';
import notifications, {GenericResponseJSON} from 'notifications';
import _RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OngoingTaskResponse} from 'util/TaskRunner';

import {ReviewableQuery, ReviewableQueryVariables} from './__generated/ReviewableQuery';
import ReviewActionButton from './ReviewActionButton';
import {
  Review,
  ReviewAction,
  ReviewableOrInaccessibleGraphQL,
  ReviewLifecycleGraphQL,
  ReviewableGraphQL,
  ReviewBadgeStatus,
} from './ReviewConstants';
import {ReviewInProgressMenu} from './ReviewInProgressMenu';
import ReviewSummaryPanelMenu from './ReviewSummaryPanelMenu';

export interface DefaultBadgeSpec {
  text?: string;
  icon?: BenchlingIconName;
  style?: CSSProperties;
}

export interface ReviewBadgeProps {
  className?: string;
  reviewableId: string;
  enableReviewLifecycle?: boolean;
  defaultBadgeSpec?: DefaultBadgeSpec;
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  openHistory?: () => void;
  /** Custom rendering of additional info related to the review stage in modal */
  renderAdditionalReviewInfo?: (reviewableId: string, reviewAction: ReviewAction) => JSX.Element | null;
  /** Custom error rendering to show errors in modal. */
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
  getSendForReviewValidationError?: (reviewableId: string) => string | null;
  performanceMetadata?: {[key: string]: string}; // takes in performance metric
  unsubmittedTableNames?: Array<string>;
}

export const REVIEWABLE_QUERY = gql`
  query ReviewableQuery($reviewableId: StrictString!) @skipAudit @engComponent(name: NOTEBOOK) {
    reviewable(id: $reviewableId) {
      ... on ReviewableInterface {
        id
        review {
          ...ReviewLifecycleReviewSummaryGraphQL
          activeReviewLifecycleVersion {
            ... on ReviewLifecycleVersionNode {
              id
              terminalState
            }
          }
        }
        parentReviewLifecycles {
          id
          currentVersion {
            id
            lifecycleType
            reviewStages {
              id
              action
            }
          }
        }
        reviewSource {
          id
        }
      }
      ... on EntryNode {
        id
        hasReviewCriteria
        template {
          id
          templateCollection {
            id
          }
        }
      }
      ... on HasPermissionsInterface {
        permissions {
          admin
          appendable
          readable
          writable
          canAccept
          canReassignRlcReviewer
          canReject
          canRetract
          canSendForReview
        }
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
  ${getFragmentWrapper('ReviewLifecycleReviewSummary').fragment}
`;

export function getBadgeStatus(status?: ReviewStatus): ReviewBadgeStatus {
  switch (status) {
    case ReviewStatus.ACCEPTANCE_SNAPSHOT_IN_PROGRESS:
    case ReviewStatus.ACCEPTED:
      return ReviewBadgeStatus.ACCEPTED;
    case ReviewStatus.NEEDS_REVIEW:
    case ReviewStatus.REVIEW_SNAPSHOT_IN_PROGRESS:
      return ReviewBadgeStatus.IN_REVIEW;
    case ReviewStatus.REJECTED:
      return ReviewBadgeStatus.REJECTED;
    case ReviewStatus.ACTION_REQUIRED:
      return ReviewBadgeStatus.ACTION_REQUIRED;
    case ReviewStatus.RETRACTED:
    case null:
    default:
      return ReviewBadgeStatus.IN_PROGRESS;
  }
}

function handleInaccessible(reviewable: ReviewableOrInaccessibleGraphQL): void {
  const argForRequestAccess = getArgForRequestAccess(reviewable);
  if (argForRequestAccess) {
    if (canRequestAccess(argForRequestAccess)) {
      requestAccess(argForRequestAccess);
    } else {
      notifications.error(FileBrowserConstants.INACCESSIBLE_MESSAGE);
    }
  }
}

function getReviewCriteriaError({
  projectReviewLifecycles,
  hasReviewCriteria,
}: {
  projectReviewLifecycles: Array<ReviewLifecycleGraphQL>;
  hasReviewCriteria: boolean;
}): JSX.Element | null {
  if (
    projectReviewLifecycles?.some(
      ({currentVersion: {lifecycleType}}) =>
        lifecycleType === ReviewLifecycleType.SELF_REVIEW && hasReviewCriteria
    )
  ) {
    return (
      <div>
        Please go to the <b>Review</b> tab and remove <b>all review criteria</b> before proceeding to
        self-review
      </div>
    );
  }
  return null;
}

/**
 * Content of ReviewBadge when a reviewable is available and accessible.
 * Currently handles two types of statuses depending on whether the status is associated with a review lifecycle.
 *
 * For accepted, rejected, and in review reviewables, if the associated review is linked to a review lifecycle,
 * show a clickable button that drops down to render a ReviewLifecycleSummaryPanel. Otherwise, just show review status.
 *
 * For in progress reviewables, if the project has a review lifecycle and the user has send for review permissions,
 * display a clickable dropdown to allow them to send for review. Otherwise, just show review status.
 */
function ReviewBadgeContent({
  reviewable,
  reviewableId,
  enableReviewLifecycle,
  defaultBadgeSpec,
  onReviewChange,
  renderAdditionalReviewInfo,
  renderReviewError,
  getSendForReviewValidationError,
  performanceMetadata,
  openHistory,
  refetchCallback,
  unsubmittedTableNames,
}: {
  reviewable: ReviewableGraphQL;
  reviewableId: string;
  enableReviewLifecycle?: boolean;
  defaultBadgeSpec?: DefaultBadgeSpec;
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  openHistory?: () => void;
  renderAdditionalReviewInfo?: (reviewableId: string, reviewAction: ReviewAction) => JSX.Element | null;
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
  getSendForReviewValidationError?: (reviewableId: string) => string | null;
  performanceMetadata?: {[key: string]: string};
  refetchCallback: () => Promise<void>;
  unsubmittedTableNames?: Array<string>;
}): JSX.Element {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [disableOptions, setDisableOptions] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState<string | null>(null);

  const hasReviewCriteria = isReviewableEntry(reviewable) ? reviewable.hasReviewCriteria : false;
  const {review} = reviewable;
  const reviewId = review?.id;
  const permissions = reviewable.permissions;

  const reviewableReviewUsesReviewLifecycle = review?.reviewLifecycle != null;
  const projectReviewLifecycles = reviewable.parentReviewLifecycles;

  const errorComponent = getReviewCriteriaError({projectReviewLifecycles, hasReviewCriteria});
  const reviewError: ReviewError = useMemo(
    () =>
      errorComponent
        ? {type: ReviewErrorType.COMPONENT, error: errorComponent}
        : getSendForReviewValidationError
          ? {type: ReviewErrorType.TOOLTIP, error: getSendForReviewValidationError}
          : {
              type: ReviewErrorType.CALLBACK,
              error: renderReviewError,
            },
    [errorComponent, getSendForReviewValidationError, renderReviewError]
  );

  const badgeStatus = getBadgeStatus(review?.status);

  // The type predicate is helpful for convincing TS that projectReviewLifecycle is not null if
  // showSendForReview is true.
  const showSendForReview = ((projectReviewLifecycles: Array<ReviewLifecycleGraphQL>): boolean => {
    if (
      !enableReviewLifecycle ||
      !permissions.canSendForReview ||
      projectReviewLifecycles.length === 0 ||
      !ReviewUtil.canSendForReviewFromStatus(review?.status)
    ) {
      return false;
    }

    if (review?.status === ReviewStatus.REJECTED) {
      // Only show send for review for rejected reviewables that were rejected using legacy review process.
      // If it was rejected using a review lifecycle, we should show the summary panel.
      return !reviewableReviewUsesReviewLifecycle;
    }
    return true;
  })(projectReviewLifecycles);

  const showSummaryPanel =
    !showSendForReview &&
    reviewableReviewUsesReviewLifecycle &&
    badgeStatus !== ReviewBadgeStatus.IN_PROGRESS;

  const badgeContext: ReviewBadgeContextValue = useMemo(
    () => ({
      reviewableId,
      reviewable,
      isDropdownOpen,
      setIsDropdownOpen,
      reviewError,
      onReviewChange,
      performanceMetadata,
      renderAdditionalReviewInfo,
      openHistory,
      reviewId,
      unsubmittedTableNames,
    }),
    [
      reviewableId,
      reviewable,
      isDropdownOpen,
      setIsDropdownOpen,
      onReviewChange,
      performanceMetadata,
      reviewError,
      renderAdditionalReviewInfo,
      openHistory,
      reviewId,
      unsubmittedTableNames,
    ]
  );

  const button = (
    <ReviewActionButton
      mode={badgeStatus}
      text={ReviewUtil.getStatusTextFromReview(
        review?.status,
        review?.activeReviewLifecycleVersion?.terminalState,
        defaultBadgeSpec?.text
      )}
      isDropdown={showSendForReview || showSummaryPanel}
      icon={
        <BenchlingIcon
          className="reviewBadge-icon"
          icon={ReviewUtil.getStatusIcon(review?.status, defaultBadgeSpec?.icon)}
        />
      }
      defaultBadgeStyle={defaultBadgeSpec?.style}
    />
  );

  let dropdown;
  if (showSendForReview || showSummaryPanel) {
    const onToggleDropdown = (isDropdownOpen: boolean): void => {
      setIsDropdownOpen(isDropdownOpen);

      if (isDropdownOpen && showSendForReview) {
        // Compute the send-for-review validation error directly from the prop rather than from
        // `reviewError`. `reviewError` is a single-winner priority chain, so a review-criteria COMPONENT
        // error (which only disables self-review) would otherwise mask a send-for-review validation error
        // such as unsaved changes, leaving "Send for review" incorrectly enabled.
        const sendForReviewValidationError = getSendForReviewValidationError?.(reviewableId) ?? null;
        const newOptionsDisabledState = sendForReviewValidationError != null;

        if (newOptionsDisabledState !== disableOptions) {
          setDisableOptions(newOptionsDisabledState);
        }
        if (tooltipTitle !== sendForReviewValidationError) {
          setTooltipTitle(sendForReviewValidationError ?? null);
        }
      }
    };
    // Render with the Review Lifecycle Summary dropdown if it is present
    dropdown = (
      <Dropdown
        closeOnInsideClick={true}
        closeOnOutsideClick={true}
        isOpen={isDropdownOpen}
        onToggle={onToggleDropdown}
        menu={
          <ReviewBadgeContext.Provider value={badgeContext}>
            {showSummaryPanel ? (
              <ReviewSummaryPanelMenu permissions={permissions} />
            ) : (
              <ReviewInProgressMenu disableOptions={disableOptions} tooltipTitle={tooltipTitle} />
            )}
          </ReviewBadgeContext.Provider>
        }
      >
        {button}
      </Dropdown>
    );
  }

  return (
    <ReviewBadgeContext.Provider value={badgeContext}>
      <RealtimeUpdateSubscriptionWrapper
        apiId={reviewableId}
        refetch={refetchCallback}
        messageFilter="no_filter"
      >
        {dropdown ?? button}
      </RealtimeUpdateSubscriptionWrapper>
    </ReviewBadgeContext.Provider>
  );
}

// TODO (BNCH-112863) This wrapper forces the ReviewBadge benchling query to get re-run after a review change.
// The function was added because the Apollo GraphQL 3.4.9 -> 3.11 upgrade is more lenient on caching,
// which causes the review badge info to not necessarily get correctly updated. Explicitly refetching
// during the onReviewChange call matches the 3.4.9 behavior until the logical flow can be revisited and
// maybe redesigned.
// Lots of typescript non-sense here, but essentially this is:
// return function(args) {
//   refetch();
//   return onReviewChange(args);
// }
export function wrapOnReviewChangeWithRefetch(
  refetch: () => Promise<unknown>,
  onReviewChange:
    | ((
        reviewableId: string,
        review: Review,
        filePermissions: Permissions,
        snapshotTask: OngoingTaskResponse | null
      ) => void)
    | undefined
): (
  reviewableId: string,
  review: Review,
  filePermissions: Permissions,
  snapshotTask: OngoingTaskResponse | null
) => void {
  return function (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ): void {
    refetch().then(() => onReviewChange?.(reviewableId, review, filePermissions, snapshotTask));
  };
}

/**
 * Displays review status of a reviewable object.
 * This component handles the GraphQL query to fetch the reviewable object and its review status, and showing
 * loading and failure states. ReviewBadgeContent is responsible for rendering the badge and dropdown menu.
 */
function ReviewBadge({
  reviewableId,
  enableReviewLifecycle,
  defaultBadgeSpec,
  onReviewChange,
  renderAdditionalReviewInfo,
  renderReviewError,
  getSendForReviewValidationError,
  performanceMetadata,
  openHistory,
  unsubmittedTableNames,
}: ReviewBadgeProps): JSX.Element | null {
  const result = useBenchlingQuery<ReviewableQuery, ReviewableQueryVariables>(REVIEWABLE_QUERY, {
    variables: {reviewableId},
    fetchPolicy: 'cache-and-network',
  });

  const refetch = result.refetch;
  const refetchCallback = useCallback(async (): Promise<void> => {
    await refetch({reviewableId});
  }, [refetch, reviewableId]);

  const onReviewChangeWrapped = wrapOnReviewChangeWithRefetch(refetch, onReviewChange);

  const {reviewable} = result.type === QueryResultType.SUCCESS ? result.data : {reviewable: undefined};

  if (result.type === QueryResultType.PENDING) {
    return (
      <div className={css(reviewStyles.throbber)}>
        <HorizontalThrobber />
      </div>
    );
  } else if (result.type === QueryResultType.ERROR || !reviewable || isInaccessible(reviewable)) {
    if (isInaccessible(reviewable)) {
      handleInaccessible(reviewable);
    }
    return null;
  } else {
    return (
      <ReviewBadgeContent
        reviewable={reviewable as ReviewableGraphQL}
        reviewableId={reviewableId}
        enableReviewLifecycle={enableReviewLifecycle}
        defaultBadgeSpec={defaultBadgeSpec}
        onReviewChange={onReviewChangeWrapped}
        renderAdditionalReviewInfo={renderAdditionalReviewInfo}
        renderReviewError={renderReviewError}
        getSendForReviewValidationError={getSendForReviewValidationError}
        performanceMetadata={performanceMetadata}
        openHistory={openHistory}
        refetchCallback={refetchCallback}
        unsubmittedTableNames={unsubmittedTableNames}
      />
    );
  }
}

const reviewStyles = StyleSheet.create({
  throbber: {
    verticalAlign: 'middle',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 10,
  },
});

export default React.memo(ReviewBadge);

let RealtimeUpdateSubscriptionWrapper = _RealtimeUpdateSubscriptionWrapper;

export function mock(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function MockRealtimeUpdateSubscriptionWrapper(props: any): null | JSX.Element {
    return props.children;
  }
  RealtimeUpdateSubscriptionWrapper = MockRealtimeUpdateSubscriptionWrapper;
}

export function restore(): void {
  RealtimeUpdateSubscriptionWrapper = _RealtimeUpdateSubscriptionWrapper;
}
